import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  useParticipants,
  useRoomContext,
  useChat
} from '@livekit/components-react'
import { RemoteParticipant } from 'livekit-client'
import { PaperAirplaneIcon, UserGroupIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import {
  useSaveChatMessageMutation,
  useLazyGetChatMessagesQuery,
  ChatMessage as ApiChatMessage
} from '../../../app/services/meseeknaApiV2'
import { useTypedSelector } from '../../../app/store'

type PrivateConversation = {
  id: string
  participants: RemoteParticipant[]
  participantNames: string[]
  lastMessage?: EnhancedChatMessage
  unreadCount: number
}

type EnhancedChatMessage = {
  id: string
  message: string
  timestamp: number
  from: {
    identity: string
    name?: string
  }
  isPrivate: boolean
  recipientIdentities?: string[]
  conversationId?: string
}

type LiveKitEnhancedChatProps = {
  className?: string
  style?: React.CSSProperties
  allowPrivateMessages?: boolean
  roomId?: string // Database room ID for message persistence
}

const LiveKitEnhancedChat: React.FC<LiveKitEnhancedChatProps> = ({
  className = '',
  style,
  allowPrivateMessages = true,
  roomId
}) => {
  const room = useRoomContext()
  const liveKitParticipants = useParticipants()
  const { send: sendPublicMessage, isSending, chatMessages: publicMessages } = useChat()

  // Get collaboration participants from Redux as fallback for private chat
  const collaborationParticipants = useTypedSelector(state => state.collaborationSlice.participants)
  const currentUserId = useTypedSelector(state => state.collaborationSlice.currentUserId)

  // Use LiveKit participants if available, otherwise create virtual participants from collaboration state
  // This ensures private chat works even when LiveKit isn't fully connected
  const participants = useMemo(() => {
    // If LiveKit has participants, use them
    if (liveKitParticipants.length > 0) {
      return liveKitParticipants
    }

    // Otherwise, create virtual participant-like objects from collaboration state
    // These won't be true RemoteParticipants but will have compatible shape for display
    return collaborationParticipants
      .filter(p => p.userId !== currentUserId)
      .map(p => ({
        identity: p.userId?.toString() || p.id,
        name: `${p.firstName} ${p.lastName}`.trim() || `User ${p.userId}`,
        sid: p.id,
        // Flag to indicate this is a virtual participant
        _isVirtual: true
      })) as unknown as (RemoteParticipant & { _isVirtual?: boolean })[]
  }, [liveKitParticipants, collaborationParticipants, currentUserId])

  // Chat persistence hooks
  const [saveChatMessage] = useSaveChatMessageMutation()
  const [getChatMessages] = useLazyGetChatMessagesQuery()
  const [persistedMessages, setPersistedMessages] = useState<EnhancedChatMessage[]>([])
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false)

  // Private messaging state
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [privateConversations, setPrivateConversations] = useState<Map<string, PrivateConversation>>(new Map())
  const [privateMessages, setPrivateMessages] = useState<Map<string, EnhancedChatMessage[]>>(new Map())
  const [messageInput, setMessageInput] = useState('')
  const [showParticipantSelector, setShowParticipantSelector] = useState(false)

  // UI state
  const [chatMode, setChatMode] = useState<'public' | 'private'>('public')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Load chat history when component mounts or roomId changes
  useEffect(() => {
    const loadChatHistory = async () => {
      if (!roomId || hasLoadedHistory) return

      try {
        console.log('📜 Loading chat history for room:', roomId)
        const result = await getChatMessages({ roomId, limit: 100 }).unwrap()

        if (result.data && result.data.length > 0) {
          // Convert API messages to enhanced format
          const historicalMessages: EnhancedChatMessage[] = result.data.map((msg: ApiChatMessage) => ({
            id: msg.id,
            message: msg.message,
            timestamp: new Date(msg.createdAt).getTime(),
            from: {
              identity: msg.senderUserId.toString(),
              name: msg.senderName
            },
            isPrivate: msg.messageType === 'private',
            recipientIdentities: msg.recipientIds?.map(id => id.toString()),
            conversationId: undefined
          }))

          setPersistedMessages(historicalMessages)
          console.log(`✅ Loaded ${historicalMessages.length} messages from history`)
        }
        setHasLoadedHistory(true)
      } catch (error) {
        console.error('❌ Failed to load chat history:', error)
        setHasLoadedHistory(true) // Don't retry on error
      }
    }

    loadChatHistory()
  }, [roomId, hasLoadedHistory, getChatMessages])

  // Convert public messages to enhanced format and merge with persisted messages
  const enhancedPublicMessages = useMemo(() => {
    const liveMessages = publicMessages
      .filter(msg => msg.from) // Filter out messages without a 'from' field
      .map(msg => ({
        id: msg.id || `${msg.timestamp}-${Math.random()}`,
        message: msg.message,
        timestamp: msg.timestamp,
        from: {
          identity: msg.from!.identity,
          // Ensure we always have a display name - fall back to identity if name is not set
          name: msg.from!.name || msg.from!.identity || 'Unknown User'
        },
        isPrivate: false
      }))

    // Filter persisted messages to only include public ones
    const historicalPublicMessages = persistedMessages.filter(msg => !msg.isPrivate)

    // Combine historical and live messages, avoiding duplicates by ID
    const liveMessageIds = new Set(liveMessages.map(m => m.id))
    const combinedMessages = [
      ...historicalPublicMessages.filter(m => !liveMessageIds.has(m.id)),
      ...liveMessages
    ]

    // Sort by timestamp
    return combinedMessages.sort((a, b) => a.timestamp - b.timestamp)
  }, [publicMessages, persistedMessages])

  // Get current conversation messages
  const currentMessages = useMemo(() => {
    if (chatMode === 'public') {
      return enhancedPublicMessages
    }

    if (activeConversationId) {
      return privateMessages.get(activeConversationId) || []
    }

    return []
  }, [chatMode, activeConversationId, enhancedPublicMessages, privateMessages])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentMessages])

  // Set up private message listener
  useEffect(() => {
    if (!room) return

    const handleDataReceived = (payload: Uint8Array, participant: RemoteParticipant | undefined) => {
      try {
        const message = new TextDecoder().decode(payload)
        const data = JSON.parse(message)

        if (data.type === 'private_message' && participant) {
          const privateMessage: EnhancedChatMessage = {
            id: data.id || `${Date.now()}-${Math.random()}`,
            message: data.message,
            timestamp: data.timestamp || Date.now(),
            from: {
              identity: participant.identity,
              name: participant.name
            },
            isPrivate: true,
            recipientIdentities: data.recipientIdentities,
            conversationId: data.conversationId
          }

          // Add to private messages
          const conversationId = data.conversationId
          setPrivateMessages(prev => {
            const updated = new Map(prev)
            const existing = updated.get(conversationId) || []
            updated.set(conversationId, [...existing, privateMessage])
            return updated
          })

          // Update conversation
          updateConversation(conversationId, participant, privateMessage)
        }
      } catch (error) {
        console.error('Error handling private message:', error)
      }
    }

    room.on('dataReceived', handleDataReceived)

    return () => {
      room.off('dataReceived', handleDataReceived)
    }
  }, [room])

  const updateConversation = (conversationId: string, participant: RemoteParticipant, lastMessage: EnhancedChatMessage) => {
    setPrivateConversations(prev => {
      const updated = new Map(prev)
      const existing = updated.get(conversationId)

      if (existing) {
        updated.set(conversationId, {
          ...existing,
          lastMessage,
          unreadCount: activeConversationId === conversationId ? 0 : existing.unreadCount + 1
        })
      } else {
        // Create new conversation
        updated.set(conversationId, {
          id: conversationId,
          participants: [participant],
          participantNames: [participant.name || participant.identity],
          lastMessage,
          unreadCount: activeConversationId === conversationId ? 0 : 1
        })
      }

      return updated
    })
  }

  const generateConversationId = (participantIdentities: string[]): string => {
    const sorted = [room?.localParticipant?.identity, ...participantIdentities]
      .filter(Boolean)
      .sort()
    return sorted.join('-')
  }

  const sendMessage = async () => {
    if (!messageInput.trim()) return

    const messageText = messageInput.trim()

    if (chatMode === 'public') {
      // Send public message using LiveKit's built-in chat
      await sendPublicMessage(messageText)

      // Persist to database if roomId is available
      if (roomId && room?.localParticipant) {
        try {
          const senderUserId = parseInt(room.localParticipant.identity) || 0
          const senderName = room.localParticipant.name || room.localParticipant.identity

          await saveChatMessage({
            roomId,
            senderUserId,
            senderName,
            message: messageText,
            messageType: 'public'
          }).unwrap()

          console.log('💾 Chat message persisted to database')
        } catch (error) {
          console.warn('⚠️ Failed to persist chat message:', error)
          // Don't fail the send - the message was still delivered via LiveKit
        }
      }
    } else {
      // Send private message
      await sendPrivateMessage(messageText)
    }

    setMessageInput('')
  }

  const sendPrivateMessage = async (message: string) => {
    if (!room || !activeConversationId) return

    const conversation = privateConversations.get(activeConversationId)
    if (!conversation) return

    const recipientIdentities = conversation.participants.map(p => p.identity)

    const privateMessage = {
      type: 'private_message',
      id: `${Date.now()}-${Math.random()}`,
      message,
      timestamp: Date.now(),
      conversationId: activeConversationId,
      recipientIdentities,
      from: {
        identity: room.localParticipant.identity,
        name: room.localParticipant.name
      }
    }

    try {
      // Send to specific participants using data channel
      const data = new TextEncoder().encode(JSON.stringify(privateMessage))

      // LiveKit's publishData with destinationIdentities for private messaging
      await room.localParticipant.publishData(data, {
        reliable: true,
        destinationIdentities: recipientIdentities
      })

      // Add to our local state (sender's copy)
      const localMessage: EnhancedChatMessage = {
        id: privateMessage.id,
        message: privateMessage.message,
        timestamp: privateMessage.timestamp,
        from: {
          identity: room.localParticipant.identity,
          name: room.localParticipant.name
        },
        isPrivate: true,
        recipientIdentities,
        conversationId: activeConversationId
      }

      setPrivateMessages(prev => {
        const updated = new Map(prev)
        const existing = updated.get(activeConversationId) || []
        updated.set(activeConversationId, [...existing, localMessage])
        return updated
      })

    } catch (error) {
      console.error('Error sending private message:', error)
    }
  }

  const startPrivateConversation = (selectedParticipants: RemoteParticipant[]) => {
    if (selectedParticipants.length === 0) return

    const participantIdentities = selectedParticipants.map(p => p.identity)
    const conversationId = generateConversationId(participantIdentities)

    // Create conversation if it doesn't exist
    if (!privateConversations.has(conversationId)) {
      setPrivateConversations(prev => {
        const updated = new Map(prev)
        updated.set(conversationId, {
          id: conversationId,
          participants: selectedParticipants,
          participantNames: selectedParticipants.map(p => p.name || p.identity),
          unreadCount: 0
        })
        return updated
      })

      // Initialize empty message list
      setPrivateMessages(prev => {
        const updated = new Map(prev)
        if (!updated.has(conversationId)) {
          updated.set(conversationId, [])
        }
        return updated
      })
    }

    setActiveConversationId(conversationId)
    setChatMode('private')
    setShowParticipantSelector(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className={`flex flex-col h-full bg-white text-gray-900 ${className}`} style={style}>
      {/* Chat Header */}
      <div className="flex flex-col gap-2 p-2 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setChatMode('public')}
            className={`px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 ${
              chatMode === 'public'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <UserGroupIcon className="w-3 h-3" />
            Public
          </button>

          {allowPrivateMessages && (
            <button
              onClick={() => setChatMode('private')}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1 ${
                chatMode === 'private'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
            >
              <ChatBubbleLeftRightIcon className="w-3 h-3" />
              Private
            </button>
          )}

          {/* Private conversation selector - inline */}
          {chatMode === 'private' && allowPrivateMessages && (
            <button
              onClick={() => setShowParticipantSelector(!showParticipantSelector)}
              className="ml-auto px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              + New
            </button>
          )}
        </div>
      </div>

      {/* Participant Selector for Private Messages */}
      {showParticipantSelector && chatMode === 'private' && (
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            👥 Start private conversation with:
          </h4>
          <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
            {participants.length > 0 ? (
              participants
                .filter((p) => {
                  // Filter out the local participant (works for both real and virtual participants)
                  const localIdentity = room?.localParticipant?.identity
                  const localUserIdStr = currentUserId?.toString()
                  return p.identity !== localIdentity && p.identity !== localUserIdStr
                })
                .map(participant => (
                  <button
                    key={participant.identity || participant.sid}
                    onClick={() => startPrivateConversation([participant as RemoteParticipant])}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors duration-200 flex items-center gap-2"
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    {participant.name || participant.identity}
                  </button>
                ))
            ) : (
              <div className="text-sm text-gray-500 py-2">
                No other participants available for private chat
              </div>
            )}
          </div>
        </div>
      )}

      {/* Private Conversations List */}
      {chatMode === 'private' && privateConversations.size > 0 && (
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="p-3">
            <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
              💬 Active Conversations
            </h4>
            <div className="space-y-1 max-h-20 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
              {Array.from(privateConversations.values()).map(conversation => (
                <button
                  key={conversation.id}
                  onClick={() => {
                    setActiveConversationId(conversation.id)
                    // Mark as read
                    setPrivateConversations(prev => {
                      const updated = new Map(prev)
                      updated.set(conversation.id, { ...conversation, unreadCount: 0 })
                      return updated
                    })
                  }}
                  className={`block w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${
                    activeConversationId === conversation.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate font-medium">
                      {conversation.participantNames.join(', ')}
                    </span>
                    {conversation.unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 ml-1 animate-pulse">
                        {conversation.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white scrollbar-thin scrollbar-thumb-gray-300">
        {currentMessages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-12">
            <div className="mb-3 text-2xl">💬</div>
            {chatMode === 'public' ? (
              <>
                <div className="font-medium mb-1">No messages yet</div>
                <div className="text-xs text-gray-400">Start the conversation!</div>
              </>
            ) : (
              <>
                <div className="font-medium mb-1">
                  {activeConversationId ? 'Private conversation ready' : 'Select a conversation'}
                </div>
                <div className="text-xs text-gray-400">
                  {activeConversationId ? 'Start your private conversation!' : 'Select a conversation or start a new one'}
                </div>
              </>
            )}
          </div>
        ) : (
          currentMessages.map((msg) => {
            // BUG FIX: Use multiple sources to determine if message is from current user
            // Priority: 1) LiveKit room identity, 2) Redux currentUserId
            // Convert both to strings for reliable comparison
            const localIdentity = room?.localParticipant?.identity || currentUserId?.toString()
            const msgIdentity = msg.from.identity
            const isOwnMessage = !!(localIdentity && msgIdentity && localIdentity === msgIdentity)
            // Always display a sender name - use name, then identity, then fallback
            const senderName = msg.from.name || msg.from.identity || 'Unknown User'

            return (
              <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-xl text-sm shadow-sm ${
                  isOwnMessage
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : msg.isPrivate
                      ? 'bg-purple-600 text-white rounded-bl-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  {/* Always show sender name for clarity */}
                  <div className={`font-medium text-xs mb-1.5 opacity-75 flex items-center gap-1 ${
                    isOwnMessage ? 'text-blue-200 justify-end' : ''
                  }`}>
                    <div className={`w-2 h-2 ${isOwnMessage ? 'bg-blue-300' : 'bg-green-400'} rounded-full`}></div>
                    {isOwnMessage ? 'You' : senderName}
                    {msg.isPrivate && <span className={isOwnMessage ? 'text-blue-200' : 'text-purple-300'}>🔒</span>}
                  </div>
                  <div className="break-words leading-relaxed">{msg.message}</div>
                  <div className={`text-xs mt-1.5 opacity-75 ${
                    isOwnMessage ? 'text-blue-200' : msg.isPrivate ? 'text-purple-200' : 'text-gray-500'
                  }`}>
                    {formatTimestamp(msg.timestamp)}
                    {isOwnMessage && <span className="ml-1">✓</span>}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-3 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              chatMode === 'public'
                ? '💬 Type your message...'
                : activeConversationId
                  ? '🔒 Type private message...'
                  : 'Select a conversation first'
            }
            disabled={chatMode === 'private' && !activeConversationId}
            className="flex-1 px-4 py-2.5 bg-gray-50 text-gray-900 border border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          />
          <button
            onClick={sendMessage}
            disabled={!messageInput.trim() || isSending || (chatMode === 'private' && !activeConversationId)}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-md"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            {isSending && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
          </button>
        </div>

        {/* Chat mode indicator */}
        <div className="mt-2 text-xs text-gray-400 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {chatMode === 'public' ? (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Public chat - visible to everyone
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                {activeConversationId ? 'Private conversation' : 'No conversation selected'}
              </span>
            )}
          </div>
          <div className="text-gray-500">Press Enter to send</div>
        </div>
      </div>
    </div>
  )
}

export default LiveKitEnhancedChat